import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';

const DeviceContext = createContext();

export const DeviceProvider = ({ children }) => {
  const [deviceData, setDeviceData] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);
  const latestSearchRef = useRef('');
  const fetchInProgressRef = useRef(false);
  const countsFetchedRef = useRef(false);
  const pageCacheRef = useRef(new Map());
  const lastSearchTermRef = useRef('');
  const [totalActiveDevices, setTotalActiveDevices] = useState(0);
  const [totalInactiveDevices, setTotalInactiveDevices] = useState(0);

  const fetchActiveInactiveCounts = useCallback(async () => {
    if (countsFetchedRef.current) return;
    countsFetchedRef.current = true;

    let page = 0;
    let allDevices = [];
    let hasNextPage = true;

    while (hasNextPage) {
      const apiUrl = apiUrls.allDevices('', page);
      try {
        const resp = await axiosInstance.get(apiUrl);
        const devices = resp.data.data;
        allDevices = allDevices.concat(devices);
        hasNextPage = resp.data.hasNext;
        page += 1;
      } catch (error) {
        console.error('[DeviceContext] Error fetching page', page, error);
        break;
      }
    }

    const activeCount = allDevices.filter((d) => d.active).length;
    const inactiveCount = allDevices.length - activeCount;

    setTotalActiveDevices(activeCount);
    setTotalInactiveDevices(inactiveCount);
  }, []);

  const fetchDeviceAttributes = useCallback(async (deviceName) => {
    const keys =
      'warranty_date,location,app_version,device_type,patch_v,group_id,history,modbus_config_name,patch_name,config_status,patch_status,modbus_v';

    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      if (attrMap.group_id) {
        localStorage.setItem('groupID', attrMap.group_id);
      }

      return {
        warranty: attrMap.warranty_date ?? null,
        location: attrMap.location ?? '',
        app_version: attrMap.app_version ?? '-',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? '-',
        group_id: attrMap.group_id ?? null,
        history: attrMap.history ?? [],
        modbus_config_name: attrMap.modbus_config_name ?? '-',
        patch_name: attrMap.patch_name ?? '-',
        config_status: attrMap.config_status ?? '-',
        patch_status: attrMap.patch_status ?? '-',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }

      console.error('Error fetching attributes:', error);
      return null;
    }
  }, []);

  const fetchDeviceAssetId = async (deviceId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getRelationInfo(deviceId));

      const assetRelation = resp.data.find(
        (r) => r.from?.entityType === 'ASSET',
      );

      return assetRelation?.from?.id || null;
    } catch (error) {
      console.error('Error fetching device asset relation:', error);
      return null;
    }
  };

  const fetchAssetAdditionalInfo = async (assetId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getAdditionalInfo(assetId));
      return resp.data?.additionalInfo || {};
    } catch (error) {
      console.error('Error fetching asset info:', error);
      return {};
    }
  };

  const fetchAllDevices = useCallback(
    async (serialNumber = '', page = 0, sortOrder = 'DESC') => {
      latestSearchRef.current = serialNumber;

      const pageNumber = typeof page === 'number' && page >= 0 ? page : 0;
      const fetchKey = `${serialNumber}_${pageNumber}_${sortOrder}`;

      // Clear cache when search term changes
      if (lastSearchTermRef.current !== serialNumber) {
        pageCacheRef.current.clear();
        lastSearchTermRef.current = serialNumber;
      }

      // Serve from cache if available
      const cached = pageCacheRef.current.get(fetchKey);
      if (cached) {
        setDeviceData(cached.devices);
        setHasNext(cached.hasNext);
        setTotalPages(cached.totalPages);
        setTotalDevices(cached.totalDevices);
        setLoadingDevices(false);
        return;
      }

      if (fetchInProgressRef.current === fetchKey) {
        return;
      }
      fetchInProgressRef.current = fetchKey;

      setLoadingDevices(true);

      try {
        let resp;

        try {
          const apiUrl = apiUrls.allDevices(serialNumber, pageNumber, sortOrder);
          resp = await axiosInstance.get(apiUrl);
        } catch (apiError) {
          setLoadingDevices(false);
          fetchInProgressRef.current = null;
          return;
        }

        const devices = resp.data.data;

        const paginationMeta = {
          hasNext: resp.data.hasNext,
          totalPages: resp.data.totalPages ?? 0,
          totalDevices: resp.data.totalElements ?? devices.length,
        };

        setHasNext(paginationMeta.hasNext);
        setTotalPages(paginationMeta.totalPages);
        setTotalDevices(paginationMeta.totalDevices);

        // Fetch active/inactive counts in the background on initial load
        if (pageNumber === 0 && !serialNumber) {
          fetchActiveInactiveCounts();
        }

        // Phase 1: Fetch attributes in parallel and render immediately
        let attrCallCount = 0;
        const phase1 = await Promise.all(
          devices.map(async (d) => {
            attrCallCount++;
            const attrs = await fetchDeviceAttributes(d.name);
            if (!attrs) return null;

            return {
              deviceId: d?.id?.id,
              machineModel: d.label || '',
              serialNumber: d.name,
              name: d.name,
              active: d.active ?? false,
              ...attrs,
              country: '-',
              state: '-',
              noc: '-',
              plant: '-',
              history: attrs.history ?? [],
              modbus_config_name: attrs.modbus_config_name ?? '-',
              patch_name: attrs.patch_name ?? '-',
            };
          }),
        );

        const initialData = phase1.filter(Boolean);

        if (latestSearchRef.current !== serialNumber) return;
        setDeviceData(initialData);
        setLoadingDevices(false);
        fetchInProgressRef.current = null;

        // Save to cache
        pageCacheRef.current.set(fetchKey, {
          devices: initialData,
          ...paginationMeta,
        });

        // Phase 2: Backfill asset info (country/state/noc/plant) silently
        const searchSnapshot = serialNumber;
        initialData.forEach(async (device) => {
          try {
            const assetId = await fetchDeviceAssetId(device.deviceId);
            if (!assetId) {
              return;
            }

            const assetInfo = await fetchAssetAdditionalInfo(assetId);
            if (!assetInfo) return;

            if (latestSearchRef.current !== searchSnapshot) return;

            const assetFields = {
              country: assetInfo?.CountryName || '-',
              state: assetInfo?.StateName || '-',
              noc: assetInfo?.NocName || '-',
              plant: assetInfo?.DisplayName || '-',
            };

            setDeviceData((prev) =>
              prev.map((d) =>
                d.deviceId === device.deviceId ? { ...d, ...assetFields } : d,
              ),
            );

            // Update cache with asset info
            const cachedEntry = pageCacheRef.current.get(fetchKey);
            if (cachedEntry) {
              cachedEntry.devices = cachedEntry.devices.map((d) =>
                d.deviceId === device.deviceId ? { ...d, ...assetFields } : d,
              );
            }
          } catch (error) {
            console.error(
              '[DeviceContext] Error fetching asset info for',
              device.serialNumber,
              error,
            );
          }
        });
      } catch (error) {
        console.error('Error fetching devices:', error);
        setLoadingDevices(false);
        fetchInProgressRef.current = null;
      }
    },
    [fetchDeviceAttributes, fetchActiveInactiveCounts],
  );

  return (
    <DeviceContext.Provider
      value={{
        deviceData,
        loadingDevices,
        hasNext,
        totalPages,
        totalDevices,
        fetchAllDevices,
        fetchDeviceAttributes,
        fetchDeviceAssetId,
        fetchAssetAdditionalInfo,
        setDeviceData,
        totalActiveDevices,
        totalInactiveDevices,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevices = () => useContext(DeviceContext);
